/* eslint-disable no-param-reassign */
/* eslint-disable react/forbid-prop-types */
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import bindMethods from 'auto-bind';
import { NavLink } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import ReactTooltip from 'react-tooltip';
import {
  CloudDownload as DownloadIcon,
  Create as PencilIcon,
  Share as ShareIcon,
  CalendarToday as CalendarIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { faCut } from '@fortawesome/free-solid-svg-icons';
import { Menu, MenuItem } from '@mui/material';
import {
  Colors, Styling, Routes,
} from '../../constants';
import {
  updateRawAssetDetails,
  resetRawAssetDetails,
  enableEditMetadata,
  updateAsset,
  exitEditMode,
} from '../../state/editMetadata';
import UpdateAssetGameScheduleDialog from '../Exchange/UpdateAssetGameScheduleDialog/component';
import { updateRawAssetDetails as updateNonEditableRawAssetDetails } from '../../state/rawAssetDetails';
import {
  updateVideoTimeStamp,
  addAssetDetails,
  addEditableAssetDetails,
} from '../../state/previewAsset';
import { updateSearchHit, queryGamesAction } from '../../state/search';
import { generateEditableRawAssetDetails } from './utils/generateEditableRawAssetDetails';
import { isOwnerOfAsset } from '../../utils/assetDetails/isOwnerOfAsset';
import { FaIcon } from '../Shared/Icons';
import { VaultButton } from '../Shared';
import { getCurrentTime } from '../VirtualClipper/utils/videojs';
import { VAULT_MEDIA_TYPE } from '../../constants/vault';
import { selectIsUserVaultSuperAdmin, selectIsSharePermitted, KeyRoleUserAccessPermission } from '../../state/user/userOrgRole';
import { Product } from '../../state/product';
import { toggleShareDialog } from '../../state/shareDialog';
import { toggleEmailDialog } from '../../state/shareEmailDialog';
import { toggleUpdateAssetGameScheduleDialog } from '../../state/exchange/updateAssetGameScheduleDialog';
import { toggleDeleteAssets } from '../../state/deleteAssets';
import DeleteAssetsDialog from '../Search/DeleteAssetsDialog/component';
import { copyAssetsToClipboard } from '../../utils/copyAssetsToClipbaord';
import { downloadAsset, downloadSource } from '../../utils/downloadAsset';

const Container = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  height: 50px;
  padding: 6px 12px 6px 12px;
  display: flex;
  align-items: center;
  background-color: ${Colors.PAPER};
  border-bottom: 1px solid ${Colors.PAPER_DARKER_1};
  button {
    &:not(:last-child) {
      margin-right: ${Styling.DEFAULT_SPACING_HALVED}px;
    }
  }
  a {
    margin-right: ${Styling.DEFAULT_SPACING_HALVED}px;
  }
`;

const EditMetadataContainer = styled.div`
  display: flex;
  margin-left: auto;
`;

const EditButtonContainer = styled.div`
  height: 50px;
  display: ${(props) => (props.$isVisible ? 'flex' : 'none')};
  flex: 1;
  align-items: center;
`;

const NavLinkStyle = {
  textDecoration: 'none',
  cursor: 'default',
};

const EditingButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledTooltip = styled(ReactTooltip)`
  &::before {
    z-index: -1 !important;
  }
`;

class OperationArea extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);
    this.state = {
      anchorEl: null,
      shareOpen: false,
      downloadOpen: false,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      isQuerying,
      exitEditMode,
      userOrgRole: { useAs },
    } = this.props;
    const newSearchTriggered = isQuerying && !prevProps.isQuerying;
    const newUseAsSelected = !isEqual(useAs, prevProps.userOrgRole.useAs);

    if (newSearchTriggered) this.cancelEdit();

    if (newUseAsSelected) exitEditMode();

    ReactTooltip.rebuild();
  }

  openClipper(target) {
    const { rawAssetDetails: { _id } } = this.props;
    let timeStamp = 0;
    timeStamp = getCurrentTime();
    target.href = `${Routes.VIRTUAL_CLIPPER}?assetId=${_id}&timeStamp=${timeStamp}`;
  }

  enterEditMode() {
    const {
      assetDetails,
      editableAssetDetails,
      enableEditMetadata,
      rawAssetDetails,
      metaTagNameList,
      updateRawAssetDetails,
      userOrgRole: {
        loginAs: { userInfo },
      },
    } = this.props;
    const assetDetailsCopy = JSON.parse(JSON.stringify(assetDetails));
    const editableAssetDetailsCopy = JSON.parse(JSON.stringify(editableAssetDetails));
    const editableRawAssetDetails = generateEditableRawAssetDetails(
      rawAssetDetails,
      metaTagNameList,
    );
    editableRawAssetDetails.modifiedBy = userInfo.displayName;
    editableRawAssetDetails.modifiedDate = new Date().toISOString();

    enableEditMetadata(assetDetailsCopy, editableAssetDetailsCopy);
    updateRawAssetDetails(editableRawAssetDetails);
  }

  cancelEdit() {
    const {
      assetDetailsCopy,
      editableAssetDetailsCopy,
      addAssetDetails,
      addEditableAssetDetails,
      isUpdating,
      exitEditMode,
    } = this.props;
    if (isUpdating) return;

    addAssetDetails(assetDetailsCopy);
    addEditableAssetDetails(editableAssetDetailsCopy);
    exitEditMode();
  }

  async saveEdit() {
    const { editableRawAssetDetails, updateAsset, isUpdating } = this.props;
    if (isUpdating) return;
    updateAsset(editableRawAssetDetails._id);
  }

  hasEditPermission() {
    const {
      userOrgRole: { loginAs, useAs },
      rawAssetDetails,
      securityProfiles: { isLoaded, idValueMapping },
      isVaultSuperAdmin,
    } = this.props;
    const role = useAs || loginAs;
    const isOwner = isLoaded ? isOwnerOfAsset(role, rawAssetDetails, idValueMapping) : false;
    const canUserEdit = role?.userAccessPermission?.manageVideoClip ?? false;
    const hasEditPermission = (role?.userAccessPermission?.edit && ((isOwner || isVaultSuperAdmin) && canUserEdit)) ?? false;
    return hasEditPermission;
  }

  hasSharePermission() {
    const {
      userOrgRole: { loginAs, useAs },
      rawAssetDetails,
      securityProfiles: { isLoaded, idValueMapping },
      isVaultSuperAdmin,
      hasSharePermission,
    } = this.props;
    const role = useAs || loginAs;
    const isOwner = isLoaded ? isOwnerOfAsset(role, rawAssetDetails, idValueMapping) : false;
    if ((isOwner || isVaultSuperAdmin) && hasSharePermission) {
      return true;
    }
    return false;
  }

  hasDownloadPreviewPermission() {
    const {
      isVaultSuperAdmin,
    } = this.props;

    if (isVaultSuperAdmin) {
      return true;
    }
    return false;
  }

  hasDeletePermission() {
    return this.hasEditPermission();
  }

  openShareMenu(event) {
    this.setState({
      anchorEl: event.target,
      shareOpen: true,
    });
  }

  closeShareMenu() {
    this.setState({
      shareOpen: false,
    });
  }

  openDownloadMenu(event) {
    this.setState({
      anchorEl: event.target,
      downloadOpen: true,
    });
  }

  closeDownloadMenu() {
    this.setState({
      downloadOpen: false,
    });
  }

  openShareEmailDialog(selectedAsset) {
    const { toggleEmailDialog } = this.props;

    toggleEmailDialog([selectedAsset]);
    this.closeShareMenu();
  }

  openShareDialog(selectedAsset) {
    const { toggleShareDialog } = this.props;

    toggleShareDialog([selectedAsset]);
    this.closeShareMenu();
  }

  renderShareButton() {
    const { rawAssetDetails } = this.props;
    const { anchorEl, shareOpen } = this.state;
    const hasSharePermission = this.hasSharePermission();

    if (!hasSharePermission) {
      return null;
    }

    const selectedAsset = {
      ...rawAssetDetails,
      _source: {
        title: rawAssetDetails.title,
        type: rawAssetDetails.type,
        videoType: rawAssetDetails.videoType,
      },
    };

    const isLive = rawAssetDetails.type === VAULT_MEDIA_TYPE.liveVideo;

    return (
      <>
        <Menu
          open={shareOpen}
          anchorEl={anchorEl}
          onClose={() => this.closeShareMenu()}
        >
          <MenuItem
            onClick={() => this.openShareDialog(selectedAsset)}
          >
            Share to Vault
          </MenuItem>
          {!isLive
            && (
              <MenuItem
                onClick={() => this.openShareEmailDialog(selectedAsset)}
              >
                Send Email Link
              </MenuItem>
            )}
          <MenuItem
            onClick={() => {
              copyAssetsToClipboard([selectedAsset]);
              this.closeShareMenu();
            }}
          >
            Copy Link to Clipboard
          </MenuItem>
        </Menu>
        <VaultButton
          variant="primaryOnWhite"
          onClick={(e) => this.openShareMenu(e)}
          data-tip="Share"
          data-for="operations-tooltip"
        >
          <ShareIcon />
        </VaultButton>
      </>
    );
  }

  renderDeleteButton() {
    const {
      rawAssetDetails,
      toggleDeleteAssets,
      userOrgRole: { useAs },
    } = this.props;

    const selectedAsset = {
      ...rawAssetDetails,
      _source: {
        title: rawAssetDetails.title,
        type: rawAssetDetails.type,
        videoType: rawAssetDetails.videoType,
      },
    };

    const isUseAs = !isEmpty(useAs);

    return (
      <VaultButton
        id="AssetDetails.OperationArea.Delete"
        variant="primaryOnWhite"
        onClick={() => toggleDeleteAssets(true, [selectedAsset])}
        disabled={isUseAs}
        data-tip="Delete"
        data-for="operations-tooltip"
      >
        <DeleteIcon />
      </VaultButton>
    );
  }

  renderDownloadButton() {
    const {
      rawAssetDetails,
      isVaultSuperAdmin,
      isExchange,
      videoSources,
      hasSource: showSource,
      userOrgRole,
    } = this.props;
    const { anchorEl, downloadOpen } = this.state;
    const isVideoUnavailable = !videoSources.src;
    const isDisabled = rawAssetDetails.type === VAULT_MEDIA_TYPE.liveVideo
      || (rawAssetDetails.type === VAULT_MEDIA_TYPE.clip ? isVideoUnavailable : false);

    const canDownload = userOrgRole?.loginAs?.[KeyRoleUserAccessPermission].downloadAsset;
    if (!canDownload && rawAssetDetails?.sourceTeamId?.Sport === 'Football') return <></>;

    const showFull = true;
    let showPreview = this.hasDownloadPreviewPermission() && !isExchange;
    if (showPreview) {
      showPreview = (videoSources.src);
    }

    let justDownload = true;

    if (!isExchange && showFull && (showPreview || showSource)) {
      justDownload = false;
    }
    return (
      <>
        {!justDownload && (
          <Menu
            open={downloadOpen}
            anchorEl={anchorEl}
            onClose={() => this.closeDownloadMenu()}
            disabled={isDisabled}
          >
            {showFull && (
              <MenuItem
                onClick={() => {
                  downloadAsset(rawAssetDetails, isVaultSuperAdmin);
                  this.closeDownloadMenu();
                }}
              >
                Download
              </MenuItem>
            )}
            {showPreview && (
              <MenuItem
                onClick={() => {
                  downloadAsset(rawAssetDetails, isVaultSuperAdmin, true);
                  this.closeDownloadMenu();
                }}
              >
                Download Preview
              </MenuItem>
            )}
            {showSource && (
              <MenuItem
                onClick={() => {
                  downloadSource(rawAssetDetails, isVaultSuperAdmin);
                  this.closeDownloadMenu();
                }}
              >
                Download Source
              </MenuItem>
            )}
          </Menu>
        )}
        <VaultButton
          variant="primaryOnWhite"
          onClick={justDownload ? () => { downloadAsset(rawAssetDetails, isVaultSuperAdmin, false); } : (e) => this.openDownloadMenu(e)}
          data-tip="Download"
          data-for="operations-tooltip"
          disabled={isDisabled}
        >
          <DownloadIcon />
        </VaultButton>
      </>
    );
  }

  renderClipEditorButton() {
    const { rawAssetDetails: { type, fileFormat }, videoSources, isExchange } = this.props;

    if (isExchange) {
      return null;
    }

    const isClipEditorEnabled = videoSources.src
      && ((type === VAULT_MEDIA_TYPE.video && fileFormat === 'mp4') || type === VAULT_MEDIA_TYPE.liveVideo);
    return (
      <NavLink
        target="_blank"
        to={Routes.VIRTUAL_CLIPPER}
        style={NavLinkStyle}
        onClick={
          (e) => {
            if (isClipEditorEnabled) this.openClipper(e.currentTarget);
            else e.preventDefault();
          }
        }
      >
        <VaultButton
          id="AssetDetails.OperationArea.NewClip"
          variant="primaryOnWhite"
          disabled={!isClipEditorEnabled}
          data-tip="Clip Editor"
          data-for="operations-tooltip"
        >
          <FaIcon icon={faCut} />
        </VaultButton>
      </NavLink>
    );
  }

  // If exchange and has edit permission render
  renderUpdateByScheduleButton() {
    const {
      isExchange,
      rawAssetDetails: {
        _id: assetId,
        title: assetName,
        exchangeScheduleId,
      },
      userOrgRole: {
        useAs,
      },
      toggleUpdateAssetGameScheduleDialog,
    } = this.props;
    const hasEditPermission = this.hasEditPermission();
    if (!hasEditPermission || !isExchange) {
      return null;
    }

    const isUseAs = !isEmpty(useAs);
    return (
      <>
        <UpdateAssetGameScheduleDialog
          assetId={assetId}
          assetName={assetName}
        />
        <VaultButton
          id="AssetDetails.OperationArea.UpdateSchedule"
          variant="primaryOnWhite"
          disabled={isUseAs}
          onClick={() => {
            toggleUpdateAssetGameScheduleDialog(exchangeScheduleId);
          }}
          data-tip="Update by Game Schedule"
          data-for="operations-tooltip"
        >
          <CalendarIcon />
        </VaultButton>
      </>
    );
  }

  renderEditButtons() {
    const { isUpdating } = this.props;
    return (
      <EditingButtonContainer>
        <VaultButton
          id="AssetDetails.OperationArea.CancelEdit"
          variant="primaryOnWhite"
          onClick={this.cancelEdit}
          disabled={isUpdating}
        >
          Cancel
        </VaultButton>
        <VaultButton
          id="AssetDetails.OperationArea.SaveEdit"
          variant="primaryOnWhite"
          onClick={this.saveEdit}
          disabled={isUpdating}
        >
          Save
        </VaultButton>
      </EditingButtonContainer>
    );
  }

  renderEditComponent() {
    const hasEditPermission = this.hasEditPermission();
    const hasDeletePermission = this.hasDeletePermission();

    if (!hasEditPermission && !hasDeletePermission) {
      return null;
    }

    const {
      isEditable,
      userOrgRole: {
        useAs,
      },
    } = this.props;

    const isUseAs = !isEmpty(useAs);

    return (
      <>
        <EditMetadataContainer>
          <EditButtonContainer $isVisible={!isEditable}>
            {hasDeletePermission && this.renderDeleteButton()}
            {hasEditPermission && (
              <VaultButton
                id="AssetDetails.OperationArea.EditAsset"
                variant="primaryOnWhite"
                disabled={isUseAs}
                onClick={this.enterEditMode}
                data-tip="Edit"
                data-for="operations-tooltip"
              >
                <PencilIcon />
              </VaultButton>
            )}
          </EditButtonContainer>
          {hasEditPermission && isEditable && this.renderEditButtons()}
        </EditMetadataContainer>
      </>
    );
  }

  shouldRenderControls() {
    const { isAssetDetailLoaded } = this.props;

    if (!isAssetDetailLoaded) {
      return false;
    }

    const {
      userOrgRole: { loginAs, useAs },
    } = this.props;

    const role = useAs || loginAs;

    const hasDownloadPermission = role?.userAccessPermission?.download ?? false;

    const hasVirtualClipperPermission = role?.userAccessPermission?.virtualClipper ?? false;

    const hasEditPermission = this.hasEditPermission();

    if (!hasDownloadPermission && !hasVirtualClipperPermission && !hasEditPermission) {
      return false;
    }

    return true;
  }

  render() {
    if (!this.shouldRenderControls()) {
      return null;
    }

    return (
      <Container id="AssetDetails.OperationArea">
        <DeleteAssetsDialog />
        {this.renderClipEditorButton()}
        {this.renderUpdateByScheduleButton()}
        {this.renderDownloadButton()}
        {this.renderShareButton()}
        {this.renderEditComponent()}
        <StyledTooltip
          id="operations-tooltip"
          delayShow={250}
          effect="solid"
        />
      </Container>
    );
  }
}

OperationArea.propTypes = {
  isEditable: PropTypes.bool.isRequired, // supplied from Redux
  enableEditMetadata: PropTypes.func.isRequired, // supplied from Redux
  videoSources: PropTypes.object.isRequired, // supplied from Redux
  updateVideoTimeStamp: PropTypes.func.isRequired, // supplied from Redux
  isAssetDetailLoaded: PropTypes.bool.isRequired, // supplied from Redux
  assetDetails: PropTypes.array.isRequired, // supplied from Redux
  editableAssetDetails: PropTypes.array.isRequired, // supplied from Redux
  metaTagNameList: PropTypes.array.isRequired, // supplied from Redux
  rawAssetDetails: PropTypes.object.isRequired, // supplied from Redux
  editableRawAssetDetails: PropTypes.object.isRequired, // supplied from Redux
  assetDetailsCopy: PropTypes.array.isRequired, // supplied from Redux
  editableAssetDetailsCopy: PropTypes.array.isRequired, // supplied from Redux
  updateRawAssetDetails: PropTypes.func.isRequired, // supplied from Redux
  resetRawAssetDetails: PropTypes.func.isRequired, // supplied from Redux
  addAssetDetails: PropTypes.func.isRequired, // supplied from Redux
  addEditableAssetDetails: PropTypes.func.isRequired, // supplied from Redux
  userOrgRole: PropTypes.object.isRequired, // supplied from Redux
  isQuerying: PropTypes.bool.isRequired, // supplied from Redux
  securityProfiles: PropTypes.object.isRequired, // supplied from Redux
  updateNonEditableRawAssetDetails: PropTypes.func.isRequired, // supplied from Redux
  updateSearchHit: PropTypes.func.isRequired, // supplied from Redux
  queryGamesAction: PropTypes.func.isRequired, // supplied from Redux
  toggleShareDialog: PropTypes.func.isRequired, // supplied from Redux
  toggleEmailDialog: PropTypes.func.isRequired, // supplied from Redux
  toggleUpdateAssetGameScheduleDialog: PropTypes.func.isRequired, // supplied from Redux
  isVaultSuperAdmin: PropTypes.bool.isRequired, // supplied from Redux
  isExchange: PropTypes.bool.isRequired, // supplied from Redux
  hasSharePermission: PropTypes.bool.isRequired, // supplied from Redux
  hasSource: PropTypes.object,
  updateAsset: PropTypes.func.isRequired, // supplied from Redux
  isUpdating: PropTypes.bool.isRequired, // supplied from Redux
  exitEditMode: PropTypes.func.isRequired, // supplied from Redux
  toggleDeleteAssets: PropTypes.func.isRequired, // supplied from Redux
};

const mapStateToProps = (state) => ({
  isEditable: state.editMetadata.isEditable,
  isUpdating: state.editMetadata.isUpdating,
  editableRawAssetDetails: state.editMetadata.rawAssetDetails,
  assetDetailsCopy: state.editMetadata.assetDetailsCopy,
  editableAssetDetailsCopy: state.editMetadata.editableAssetDetailsCopy,
  isQuerying: state.search.isQuerying,
  videoSources: state.videoSources,
  isAssetDetailLoaded: state.previewAsset.isLoaded,
  assetDetails: state.previewAsset.assetDetails,
  editableAssetDetails: state.previewAsset.editableAssetDetails,
  metaTagNameList: state.metatags.metaTagNameList,
  rawAssetDetails: state.rawAssetDetails,
  userOrgRole: state.user.userOrgRole,
  securityProfiles: state.securityProfiles,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  isExchange: state.product.target === Product.Exchange,
  hasSharePermission: selectIsSharePermitted(state),
  hasSource: state.previewAsset.mediaInformation.sourceSource,
});

const mapDispatchToProps = {
  enableEditMetadata,
  updateVideoTimeStamp,
  updateRawAssetDetails,
  resetRawAssetDetails,
  addAssetDetails,
  addEditableAssetDetails,
  updateNonEditableRawAssetDetails,
  updateSearchHit,
  queryGamesAction,
  toggleShareDialog,
  toggleEmailDialog,
  toggleUpdateAssetGameScheduleDialog,
  toggleDeleteAssets,
  updateAsset,
  exitEditMode,
};

export default connect(mapStateToProps, mapDispatchToProps)(OperationArea);
